package com.trevorwiebe.timesheet.core.presentation.common

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.adaptive.navigationsuite.NavigationSuiteType
import androidx.compose.runtime.Composable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalWindowInfo
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.window.core.layout.WindowSizeClass

val LocalWindowSizeClass = compositionLocalOf { WindowSizeClass(0, 0) }

val LocalNavSuiteType = compositionLocalOf { NavigationSuiteType.None }

object ResponsiveDefaults {
    val contentMaxWidth = 940.dp
    val formMaxWidth = 480.dp
    val dialogMaxWidth = 560.dp

    // Window width at which screen content stops filling the window and is
    // centered at contentMaxWidth instead. Below this, content is full width.
    val contentCapMinWindowWidth = 1300.dp

    // Below this width a rail is shown, above it the full navigation drawer.
    val navDrawerMinWidth = 1100.dp
}

/**
 * True when the side rail/drawer is showing. Its entries include the More
 * screen's actions (Time Off, Admin, Sign Out), which hide themselves there.
 */
@Composable
fun isSideNavVisible(): Boolean {
    val type = LocalNavSuiteType.current
    return type == NavigationSuiteType.NavigationRail ||
        type == NavigationSuiteType.NavigationDrawer
}

@Composable
fun isAtLeastMedium(): Boolean = LocalWindowSizeClass.current
    .isWidthAtLeastBreakpoint(WindowSizeClass.WIDTH_DP_MEDIUM_LOWER_BOUND)

@Composable
fun isAtLeastExpanded(): Boolean = LocalWindowSizeClass.current
    .isWidthAtLeastBreakpoint(WindowSizeClass.WIDTH_DP_EXPANDED_LOWER_BOUND)

/**
 * Centers screen content and caps its width, but only once the window is at
 * least [capMinWindowWidth] wide; below that, content fills the window.
 * Pass capMinWindowWidth = 0.dp to always cap (e.g. narrow forms).
 */
@Composable
fun ResponsiveContent(
    modifier: Modifier = Modifier,
    maxWidth: Dp = ResponsiveDefaults.contentMaxWidth,
    capMinWindowWidth: Dp = ResponsiveDefaults.contentCapMinWindowWidth,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = modifier.fillMaxSize(),
        contentAlignment = Alignment.TopCenter,
    ) {
        Box(modifier = responsiveWidthModifier(maxWidth, capMinWindowWidth)) {
            content()
        }
    }
}

/**
 * [ResponsiveContent] for a strip that shouldn't take the full height, such as a Scaffold
 * bottomBar, so its width lines up with the screen content above it.
 */
@Composable
fun ResponsiveWidth(
    modifier: Modifier = Modifier,
    maxWidth: Dp = ResponsiveDefaults.contentMaxWidth,
    capMinWindowWidth: Dp = ResponsiveDefaults.contentCapMinWindowWidth,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = modifier.fillMaxWidth(),
        contentAlignment = Alignment.TopCenter,
    ) {
        Box(modifier = responsiveWidthModifier(maxWidth, capMinWindowWidth)) {
            content()
        }
    }
}

@Composable
private fun responsiveWidthModifier(maxWidth: Dp, capMinWindowWidth: Dp): Modifier {
    val windowWidth = with(LocalDensity.current) {
        LocalWindowInfo.current.containerSize.width.toDp()
    }
    return if (windowWidth >= capMinWindowWidth) {
        // widthIn must come before fillMaxWidth: the other way around,
        // fillMaxWidth fixes the constraints and widthIn cannot shrink them.
        Modifier.widthIn(max = maxWidth).fillMaxWidth()
    } else {
        Modifier.fillMaxWidth()
    }
}

/** Caps dialog width so dialogs don't stretch on wide windows. */
fun Modifier.responsiveDialogWidth(): Modifier =
    widthIn(max = ResponsiveDefaults.dialogMaxWidth)
